// EW-190: the admin UI must never render unauthenticated. main.tsx asks this module
// before the first paint whether the visitor goes to the IdP instead of the shell; the
// decision stays pure so it is unit-testable without MSAL.
export const LOGGED_OUT_PATH = '/logged-out';

// Set right before logoutRedirect. MSAL drops its own cache on logout, so without this
// flag the load that follows would look like "not signed in yet" and bounce straight back
// to the IdP — with a live IdP session that makes logout look broken. sessionStorage
// survives the IdP round trip in the same tab, which is exactly the scope we need.
const JUST_LOGGED_OUT_KEY = 'echoadmin.just-logged-out';

export interface LoginGateState {
  hasAccount: boolean;
  pathname: string;
  justLoggedOut: boolean;
}

export function shouldRedirectToLogin(state: LoginGateState): boolean {
  if (state.hasAccount) return false;
  if (state.justLoggedOut) return false;
  return state.pathname !== LOGGED_OUT_PATH;
}

/**
 * A signed-in visitor must never see the logged-out screen. MSAL returns the browser to
 * the page where the login started (navigateToLoginRequestUrl), so a login begun on the
 * logged-out screen lands back on it — with an account in cache, and nothing to redirect
 * it away. Bookmarks and the back button reach the same state.
 */
export function shouldLeaveLoggedOutScreen(state: Pick<LoginGateState, 'hasAccount' | 'pathname'>): boolean {
  return state.hasAccount && state.pathname === LOGGED_OUT_PATH;
}

export function markLoggedOut(): void {
  sessionStorage.setItem(JUST_LOGGED_OUT_KEY, '1');
}

/** Reads and clears the flag — it may only steer the single load that follows a logout. */
export function takeLoggedOutFlag(): boolean {
  const flagged = sessionStorage.getItem(JUST_LOGGED_OUT_KEY) !== null;
  sessionStorage.removeItem(JUST_LOGGED_OUT_KEY);
  return flagged;
}
